/**
 * فاز ۱۸ — امنیتِ عمیق: لایهٔ ذخیرهٔ توکن (access + refresh)
 *
 * پیش‌فرض (flag خاموش): دقیقاً همان رفتار فعلی — access و refresh در
 * localStorage. این یعنی صفر رگرسیون.
 *
 * وقتی mode=cookie:
 *   - access  → یک کوکیِ غیر-HttpOnly (document.cookie قابل خواندن) که خودِ
 *               مرورگر/فرانت کنترلش را دارد.
 *   - refresh → در localStorage نگه می‌شود تا فرانت بتواند آن را برای
 *               rotate/refresh بفرستد (کپیِ server-side در یک کوکیِ HttpOnly
 *               هم روی بک‌اند می‌نشیند؛ اینجا فقط برای منطقِ client-side).
 *
 * Switching is opt-in. Reading the mode happens ONCE per page-load:
 *   1. `?useCookieAuth=1` in the URL query, OR
 *   2. a persisted `kadolar_auth_mode_v1` value of 'cookie' in localStorage.
 *
 * 18-B dev escape hatch: `?useLocalStorageToken=1` forces localStorage for
 * the current page-load even when cookie mode is otherwise on — so staff
 * panels that still expect localStorage keep working.
 */

const LS_AUTH_MODE_KEY = 'kadolar_auth_mode_v1';
const LS_ACCESS = 'token';
const LS_REFRESH = 'refresh_token';
const ACCESS_COOKIE = 'kadolar_access';

function _inBrowser() {
  return typeof document !== 'undefined' && typeof window !== 'undefined';
}

function _queryFlag() {
  try {
    const qs = new URLSearchParams(window.location.search);
    return qs;
  } catch (_) {
    return new URLSearchParams();
  }
}

function _persistedMode() {
  try {
    const v = localStorage.getItem(LS_AUTH_MODE_KEY);
    return v === 'cookie' ? 'cookie' : 'local';
  } catch (_) {
    return 'local';
  }
}

/**
 * Resolve the active auth-storage mode for this page-load.
 * Returns 'cookie' or 'local'.
 */
export function resolveAuthMode() {
  if (!_inBrowser()) return 'local';
  const qs = _queryFlag();

  // 18-B dev escape hatch — always wins.
  if (qs.get('useLocalStorageToken') === '1') return 'local';

  // Opt-in cookie mode.
  if (qs.get('useCookieAuth') === '1') {
    try { localStorage.setItem(LS_AUTH_MODE_KEY, 'cookie'); } catch (_) {}
    return 'cookie';
  }

  return _persistedMode();
}

/** Persist a mode choice so subsequent loads keep it. */
export function setAuthMode(mode) {
  if (!_inBrowser()) return;
  try {
    localStorage.setItem(LS_AUTH_MODE_KEY, mode === 'cookie' ? 'cookie' : 'local');
  } catch (_) {}
}

function _readCookieValue(name) {
  if (!_inBrowser()) return null;
  try {
    const m = document.cookie
      .split('; ')
      .find((c) => c.startsWith(`${name}=`));
    return m ? decodeURIComponent(m.split('=')[1]) : null;
  } catch (_) {
    return null;
  }
}

function _writeCookie(name, value, maxAgeSec) {
  if (!_inBrowser()) return;
  try {
    const attrs = [];
    if (maxAgeSec) attrs.push(`Max-Age=${Math.floor(maxAgeSec)}`);
    attrs.push('Path=/');
    // intentionally NOT HttpOnly so the front-end can read it back.
    attrs.push('SameSite=Lax');
    if (window.location.protocol === 'https:') attrs.push('Secure');
    document.cookie = `${name}=${encodeURIComponent(value)}; ${attrs.join('; ')}`;
  } catch (_) {}
}

function _clearCookie(name) {
  if (!_inBrowser()) return;
  try {
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
  } catch (_) {}
}

const MODE = resolveAuthMode();

function _accessIsCookie() {
  return MODE === 'cookie';
}

// ---------------------------------------------------------------------------
// Access token
// ---------------------------------------------------------------------------

export function getAccessToken() {
  if (_accessIsCookie()) {
    const v = _readCookieValue(ACCESS_COOKIE);
    if (v) return v;
    // fallback mirror (in case the cookie was set but read failed)
    try {
      return localStorage.getItem(LS_ACCESS) || null;
    } catch (_) {
      return null;
    }
  }
  try {
    return localStorage.getItem(LS_ACCESS) || null;
  } catch (_) {
    return null;
  }
}

export function setAccessToken(token, ttlSeconds = 60 * 60 * 24) {
  if (!token) return;
  if (_accessIsCookie()) {
    _writeCookie(ACCESS_COOKIE, token, ttlSeconds);
    // Keep a mirror in localStorage so refresh / session-bridge logic that
    // still reads localStorage keeps working during the transition.
    try { localStorage.setItem(LS_ACCESS, token); } catch (_) {}
  } else {
    try { localStorage.setItem(LS_ACCESS, token); } catch (_) {}
  }
}

export function clearAccessToken() {
  if (_accessIsCookie()) {
    _clearCookie(ACCESS_COOKIE);
  }
  try { localStorage.removeItem(LS_ACCESS); } catch (_) {}
}

// ---------------------------------------------------------------------------
// Refresh token
// ---------------------------------------------------------------------------

export function getRefreshToken() {
  try {
    return localStorage.getItem(LS_REFRESH) || null;
  } catch (_) {
    return null;
  }
}

export function setRefreshToken(refresh, ttlSeconds = 60 * 60 * 24 * 14) {
  if (!refresh) return;
  // refresh is ALWAYS in localStorage for client-side rotation logic.
  // The server keeps a parallel HttpOnly copy for cookie-side rotation.
  try { localStorage.setItem(LS_REFRESH, refresh); } catch (_) {}
  if (_accessIsCookie()) {
    // also mirror in a (non-HttpOnly) cookie for session refresh on same tab.
    _writeCookie('kadolar_refresh_client', refresh, ttlSeconds);
  }
}

export function clearRefreshToken() {
  try { localStorage.removeItem(LS_REFRESH); } catch (_) {}
  if (_accessIsCookie()) {
    _clearCookie('kadolar_refresh_client');
  }
}

// ---------------------------------------------------------------------------
// User profile blob (kept in localStorage in BOTH modes — it's not a secret,
// it's a convenience cache of the already-authenticated user)
// ---------------------------------------------------------------------------

export function getUserBlob() {
  try {
    return localStorage.getItem('user') || null;
  } catch (_) {
    return null;
  }
}

export function setUserBlob(user) {
  try {
    if (user === null || user === undefined) {
      localStorage.removeItem('user');
    } else {
      localStorage.setItem('user', JSON.stringify(user));
    }
  } catch (_) {}
}

// ---------------------------------------------------------------------------
// Batch clear (logout / 401)
// ---------------------------------------------------------------------------

export function clearAllAuth() {
  clearAccessToken();
  clearRefreshToken();
  try { localStorage.removeItem('user'); } catch (_) {}
  if (_accessIsCookie()) {
    _clearCookie('kadolar_refresh_client');
    _clearCookie(ACCESS_COOKIE);
  }
}

// ---------------------------------------------------------------------------
// Mode inspection (for UI / tests)
// ---------------------------------------------------------------------------

export function isCookieMode() {
  return _accessIsCookie();
}

export const AUTH_STORAGE = {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
  getRefreshToken,
  setRefreshToken,
  clearRefreshToken,
  getUserBlob,
  setUserBlob,
  clearAllAuth,
  isCookieMode,
  resolveAuthMode,
  setAuthMode,
  _ACCESS_COOKIE: ACCESS_COOKIE,
};

export default AUTH_STORAGE;
